import { store } from "@store/index";
import {
  open as openDialog,
  updateParams,
} from "@store/features/layout/dialogSlice";
import { trpc } from "@utils/api";

type MigrationOperationType =
  | "DELETE_MODEL"
  | "DELETE_FIELD"
  | "DELETE_RELATIONSHIP"
  | "UPDATE_FIELD"
  | "UPDATE_DEFAULT_VALUE"
  | "DELETE_FIELD_OPTION";

type Strategy = "replace-empty" | "replace-legacy" | "replace-all";

type MigrationImpactPreviewResult = {
  impactSummary: {
    destructiveOperations: {
      operationType: string;
      resourceType: string;
      resourceId?: string;
      resourceHandle: string;
      resourceName: string;
      affectedRecords: number;
      impact: string;
      warning?: string;
      details?: Record<string, unknown>;
    }[];
    totalAffectedRecords: number;
    warnings: string[];
    blockingValidations: {
      type: string;
      resourceHandle: string;
      resourceName: string;
      affectedRecords: number;
      message: string;
      resolution: string;
      samples?: unknown[];
    }[];
  };
  requiresApproval: boolean;
  canProceed: boolean;
};

type DefaultValueOptions = {
  defaultValueStrategy: Strategy;
  originalValue: { value: unknown[]; optionIds?: string[] };
  newValue: { value: unknown[]; optionIds?: string[] };
  enabledStrategies?: Strategy[];
  fieldLabel?: string;
  allowMultiple?: boolean;
};

export async function openMigrationImpactDialog(
  operationType: MigrationOperationType,
  resourceName: string,
  resourceId: string,
  previewFn: () => Promise<MigrationImpactPreviewResult>,
  executionPayload: Record<string, unknown>,
  options?: Partial<DefaultValueOptions>,
): Promise<void> {
  store.dispatch(
    openDialog({
      schema: "MIGRATION_IMPACT",
      params: {
        operationType,
        resourceName,
        resourceId,
        impactSummary: null,
        requiresApproval: false,
        canProceed: true,
        isLoadingPreview: true,
        isExecuting: false,
        executionPayload,
        ...(options || {}),
      },
    }),
  );

  try {
    const preview = await previewFn();
    store.dispatch(
      updateParams({
        impactSummary: preview.impactSummary,
        requiresApproval: preview.requiresApproval,
        canProceed: preview.canProceed,
        isLoadingPreview: false,
      }),
    );
  } catch (error) {
    console.error("Failed to load impact preview:", error);
    store.dispatch(
      updateParams({
        isLoadingPreview: false,
        error: "Failed to load impact preview",
      }),
    );
  }
}

export async function openDeleteModelDialog(
  modelId: string,
  modelName: string,
): Promise<void> {
  await openMigrationImpactDialog(
    "DELETE_MODEL",
    modelName,
    modelId,
    async () => {
      const result = await trpc.crm.previewDeleteModel.query({ modelId });
      return result as MigrationImpactPreviewResult;
    },
    { modelId },
  );
}

export async function openDeleteFieldDialog(
  fieldId: string,
  fieldLabel: string,
): Promise<void> {
  await openMigrationImpactDialog(
    "DELETE_FIELD",
    fieldLabel,
    fieldId,
    async () => {
      const result = await trpc.crm.previewDeleteField.query({ fieldId });
      return result as MigrationImpactPreviewResult;
    },
    { fieldId },
  );
}

export async function openDeleteRelationshipDialog(
  relationshipId: string,
  relationshipName: string,
): Promise<void> {
  await openMigrationImpactDialog(
    "DELETE_RELATIONSHIP",
    relationshipName,
    relationshipId,
    async () => {
      const result = await trpc.crm.previewDeleteRelationship.query({
        relationshipId,
      });
      return result as MigrationImpactPreviewResult;
    },
    { relationshipId },
  );
}

export async function openUpdateDefaultValueDialog(
  fieldId: string,
  fieldLabel: string,
  originalValue: { value: unknown[]; optionIds?: string[] },
  newValue: { value: unknown[]; optionIds?: string[] },
  executionPayload: Record<string, unknown>,
  options?: {
    enabledStrategies?: Strategy[];
    allowMultiple?: boolean;
  },
): Promise<void> {
  const initialStrategy: Strategy = "replace-empty";

  await openMigrationImpactDialog(
    "UPDATE_DEFAULT_VALUE",
    fieldLabel,
    fieldId,
    async () => {
      const result = await trpc.crm.previewUpdateDefaultValue.query({
        fieldId,
        defaultValue: newValue.value,
        strategy: initialStrategy,
      });
      return result as MigrationImpactPreviewResult;
    },
    executionPayload,
    {
      defaultValueStrategy: initialStrategy,
      originalValue,
      newValue,
      enabledStrategies: options?.enabledStrategies || [
        "replace-empty",
        "replace-legacy",
        "replace-all",
      ],
      fieldLabel,
      allowMultiple: options?.allowMultiple ?? false,
    },
  );
}
